import React, { useState, useEffect } from "react";
import privateApi from "../../apis/privateApi";
import "./UserList.css";
import { Link } from "react-router-dom";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [deleteToken, setDeleteToken] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isBulkDelete, setIsBulkDelete] = useState(false);

    const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 1 });
    const [filters, setFilters] = useState({ email: "", role: "", isActive: "" });

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const cleaned = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));
            const q = new URLSearchParams({ ...cleaned, page: pagination.page, limit: pagination.limit }).toString();
            const res = await privateApi.get(`/api/users?${q}`);
            setUsers(res?.data?.data || []);
            setPagination(p => ({ ...p, totalPages: res?.data?.pagination?.pages || 1 }));
        } catch (err) { console.error("Failed to fetch users:", err); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const initialFetchUsers = async () => {
            try {
                setLoading(true);
                const cleaned = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));
                const q = new URLSearchParams({ ...cleaned, page: pagination.page, limit: pagination.limit }).toString();
                const res = await privateApi.get(`/api/users?${q}`);
                setUsers(res?.data?.data || []);
                setPagination(p => ({ ...p, totalPages: res?.data?.pagination?.pages || 1 }));
            } catch (err) { console.error("Failed to fetch users:", err); }
            finally { setLoading(false); }
        };
        initialFetchUsers();
    }, [pagination.page, pagination.limit, filters]);

    const handleExport = async () => {
        try {
            setExporting(true);
            const cleaned = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));
            const res = await privateApi.get(`/api/users/export?${new URLSearchParams(cleaned)}`, { responseType: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
            link.download = "Users.csv"; link.click();
        } catch { alert("You are not authorized to export this data."); }
        finally { setExporting(false); }
    };

    const openDeleteDialog = id => {
        const token = Math.random().toString(36).substring(2, 8).toUpperCase();
        setDeleteToken(token); setSelectedUserId(id); setIsBulkDelete(false); setShowConfirm(true);
    };

    const openBulkDeleteDialog = () => {
        if (selectedUserIds.length === 0) { alert("Please select at least one user to delete."); return; }
        const token = Math.random().toString(36).substring(2, 8).toUpperCase();
        setDeleteToken(token); setIsBulkDelete(true); setShowConfirm(true);
    };

    const confirmDelete = async input => {
        if (input !== deleteToken) { alert("Invalid confirmation code."); return; }
        try {
            await privateApi.delete(`/api/users/${selectedUserId}`);
            setUsers(p => p.filter(u => u._id !== selectedUserId));
        } catch { alert("Failed to delete user."); }
        setShowConfirm(false);
    };

    const confirmBulkDelete = async input => {
        if (input !== deleteToken) { alert("Invalid confirmation code."); return; }
        try {
            await privateApi.delete(`/api/users/bulk-delete`, { data: { userIds: selectedUserIds } });
            setUsers(p => p.filter(u => !selectedUserIds.includes(u._id)));
            setSelectedUserIds([]);
        } catch { alert("Failed to delete selected users."); }
        setShowConfirm(false);
    };

    const handleSelectAll = () => {
        setSelectedUserIds(selectedUserIds.length === users.length ? [] : users.map(u => u._id));
    };

    const allSelected = users.length > 0 && selectedUserIds.length === users.length;

    return (
        <div className="ul-page">
            {/* Header */}
            <div className="ul-header">
                <div className="ul-header-left">
                    <h1>Users</h1>
                    <p>Manage system accounts, roles and access permissions</p>
                </div>
                <div className="ul-header-actions">
                    <Link className="ul-btn ul-btn-primary" to="/admin/users/create">+ Add User</Link>
                    <button className="ul-btn ul-btn-danger"
                        onClick={openBulkDeleteDialog} disabled={selectedUserIds.length === 0}>
                        🗑 Delete ({selectedUserIds.length})
                    </button>
                    <button className="ul-btn ul-btn-muted" onClick={handleExport} disabled={exporting}>
                        {exporting ? "Exporting…" : "⬇ Export CSV"}
                    </button>
                </div>
            </div>

            {/* Filter bar */}
            <div className="ul-filter-bar">
                <span className="ul-filter-label">Filter</span>
                <input className="ul-filter-input" type="text" name="email"
                    placeholder="Search by email…" value={filters.email}
                    onChange={e => setFilters(p => ({ ...p, email: e.target.value }))} />
                <select className="ul-filter-select" name="role" value={filters.role}
                    onChange={e => setFilters(p => ({ ...p, role: e.target.value }))}>
                    <option value="">All Roles</option>
                    <option value="ADMIN">Admin</option>
                    <option value="STATE">State</option>
                    <option value="LGA">LGA</option>
                    <option value="USER">User</option>
                </select>
                <select className="ul-filter-select" name="isActive" value={filters.isActive}
                    onChange={e => setFilters(p => ({ ...p, isActive: e.target.value }))}>
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                </select>
                <button className="ul-btn ul-btn-primary" style={{ borderRadius: 6, padding: "7px 14px" }}
                    onClick={() => { setPagination(p => ({ ...p, page: 1 })); fetchUsers(); }}>
                    Apply
                </button>
                <div className="ul-filter-spacer" />
                <div className="ul-per-page-wrap">
                    <span className="ul-per-page-label">Rows</span>
                    <select className="ul-filter-select" style={{ minWidth: 70 }}
                        value={pagination.limit}
                        onChange={e => setPagination(p => ({ ...p, limit: Number(e.target.value), page: 1 }))}>
                        <option value={10}>10</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="ul-table-card">
                {loading ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", gap: 14 }}>
                        <div style={{ width: 40, height: 40, border: "3px solid #D2DDD5", borderTopColor: "#006B3C", borderRadius: "50%", animation: "ul-spin .8s linear infinite" }} />
                        <p style={{ fontSize: 13, color: "#6B7F70", fontWeight: 500 }}>Loading Users…</p>
                        <style>{`@keyframes ul-spin { to { transform:rotate(360deg) } }`}</style>
                    </div>
                ) : (
                    <div className="ul-table-scroll">
                        <table className="ul-table">
                            <thead>
                                <tr>
                                    <th className="checkbox-th">
                                        <input type="checkbox" checked={allSelected} onChange={handleSelectAll} />
                                    </th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.length === 0 ? (
                                    <tr><td colSpan="7" className="ul-no-data">No users found</td></tr>
                                ) : users.map(user => (
                                    <tr key={user._id} className={selectedUserIds.includes(user._id) ? "selected" : ""}>
                                        <td>
                                            <input type="checkbox" checked={selectedUserIds.includes(user._id)}
                                                onChange={() => setSelectedUserIds(p =>
                                                    p.includes(user._id) ? p.filter(id => id !== user._id) : [...p, user._id]
                                                )} />
                                        </td>
                                        <td>{user.name || "—"}</td>
                                        <td>{user.email}</td>
                                        <td>{user.roleId?.name || "—"}</td>
                                        <td>
                                            <span className={`ul-status ${user.isActive ? "active" : "inactive"}`}>
                                                <span className="ul-status-dot" />
                                                {user.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <div className="ul-actions">
                                                <Link className="ul-action-btn view" to="/admin/user" state={{ userId: user._id }} title="View"><FaEye size={13} /></Link>
                                                <Link className="ul-action-btn edit" to="/admin/users/edit" state={{ userId: user._id }} title="Edit"><FaEdit size={13} /></Link>
                                                <button className="ul-action-btn del" onClick={() => openDeleteDialog(user._id)} title="Delete"><FaTrash size={13} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            <div className="ul-pagination">
                <button className="ul-page-btn" onClick={() => setPagination(p => ({ ...p, page: Math.max(p.page - 1, 1) }))} disabled={pagination.page === 1}>
                    <FiChevronLeft size={13} /> Prev
                </button>
                <span className="ul-page-info">Page {pagination.page} of {pagination.totalPages}</span>
                <button className="ul-page-btn" onClick={() => setPagination(p => ({ ...p, page: Math.min(p.page + 1, p.totalPages) }))} disabled={pagination.page === pagination.totalPages}>
                    Next <FiChevronRight size={13} />
                </button>
            </div>

            <ConfirmDialog show={showConfirm} token={deleteToken}
                action={isBulkDelete ? "To delete selected users" : "To delete this user"}
                onCancel={() => setShowConfirm(false)}
                onConfirm={isBulkDelete ? confirmBulkDelete : confirmDelete} />
        </div>
    );
};

export default UserList;